/**
 * LeaveBalanceCard.jsx — compact "your remaining leave" strip for the Home
 * dashboard. Presentational: reads the balance payload already loaded by
 * Dashboard (/leaves/balance → { year, balances:[{leave_type,entitled,taken,remaining}] }).
 * Each pill deep-links to the Leaves page with the apply form pre-opened for
 * that leave type; low-remaining types get a gentle amber nudge.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Plane, AlertTriangle } from "lucide-react";

const label = (t) => (t || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const isLow = (b) => b.entitled > 0 && (b.remaining <= 1 || b.remaining / b.entitled <= 0.2);

export const LeaveBalanceCard = ({ balance }) => {
  const nav = useNavigate();
  const rows = balance?.balances || [];
  if (!rows.length) return null;

  return (
    <div className="mb-4 card-flat p-4" data-testid="leave-balance-card">
      <div className="flex items-center gap-2 mb-3">
        <Plane size={14} className="text-[#F54800]" />
        <span className="label-overline">Your leave balance · {balance.year}</span>
        <span className="text-[11px] text-slate-400 ml-auto hidden sm:inline">Tap a type to apply</span>
      </div>
      <div className="flex flex-wrap gap-2.5">
        {rows.map((b) => {
          const low = isLow(b);
          return (
            <button
              key={b.leave_type}
              type="button"
              onClick={() => nav(`/my-hr?tab=leaves&apply=1&type=${b.leave_type}`)}
              className={`flex-1 min-w-[120px] text-left rounded-sm border px-3 py-2 transition-colors hover:border-[#F54800] hover:shadow-sm ${low ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}
              data-testid={`leave-balance-${b.leave_type}`}
              title={`Apply for ${label(b.leave_type)} leave`}
            >
              <div className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                {label(b.leave_type)}
                {low && <AlertTriangle size={10} className="text-amber-500" data-testid={`leave-balance-low-${b.leave_type}`} />}
              </div>
              <div className="flex items-baseline gap-1">
                <span
                  className={`font-display font-semibold text-xl ${low ? "text-amber-600" : "text-slate-900"}`}
                  data-testid={`leave-balance-remaining-${b.leave_type}`}
                >
                  {b.remaining}
                </span>
                <span className="text-[11px] text-slate-400">/ {b.entitled} left</span>
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`leave-balance-taken-${b.leave_type}`}>
                {b.taken} taken this year
              </div>
              {low && <div className="text-[10px] text-amber-600 mt-0.5">Running low</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
};
